import React, { useEffect, useMemo } from 'react';
import {
  useSubscriptionPageRightPanel,
  useSubscriptionInfo,
  useSubscriptionPlans,
  useTeamInfo,
  useGetUser,
  useQueryParams,
} from 'src/hooks';
import { ManageAccountContentPage } from '../ManageAccountContentPage/ManageAccountContentPage';
import { SubscriptionPlansForm } from '../SubscriptionPlansForm';
import './SubscriptionPlans.scss';
import { PriceDiscountType } from 'src/types/wallet/models/PriceDiscountType';
import { PaymentPageRightSidePanelType, CanonicalInterval } from 'src/types';
import { UpdateMonthlyToAnnuallySection } from 'src/v2/pages/subscription/components/UpdateMonthlyToAnnuallySection';
import { LeaveWorkspaceSection } from 'src/v2/pages/subscription/components/LeaveWorkspaceSection';
import { ChangeSeatsSection } from 'src/v2/pages/manageUsersInWorkspace/components/ChangeSeatsSection';
import { ChangeSeatsCheckout } from 'src/v2/pages/subscription/components/ChangeSeatsCheckout';
import { LabelEnterprise } from 'src/components/LabelEnterprise';
import { BillingInfoSection } from 'src/v2/pages/manageUsersInWorkspace/components/BillingInfoSection';
import { ResourcesSection } from 'src/v2/pages/manageUsersInWorkspace/components/ResourcesSection';
import { AppearingFromRight } from 'src/animations/AppearingFromRight';
import { useRef } from 'react';

// TODO: rename to SubscriptionPage
export const SubscriptionPlans = () => {
  const {
    tier_id,
    isEnterpriseTier,
    isTeamsTier,
    isSubscribedTier,
    isEnterpriseTrialTier,
    isEducationUser,
  } = useGetUser();
  const checkoutContentRef = useRef<HTMLDivElement>(null);
  const { isMember, isOwner, isAdmin } = useTeamInfo();
  const { activePanelView, changeActivePanelView } =
    useSubscriptionPageRightPanel();
  const { searchParams, removeSearchParam } = useQueryParams();
  const { open_seats_section } = searchParams;

  const { subscriptionInfo, isAppleOrAmpPayment, isSubscriptionInfoLoading } =
    useSubscriptionInfo();
  const { plans } = useSubscriptionPlans({
    group: isTeamsTier ? 'teams' : 'individual',
    families: subscriptionInfo ? [subscriptionInfo.plan_family] : undefined,
    seat_count:
      subscriptionInfo && subscriptionInfo.seat_count
        ? subscriptionInfo.seat_count
        : 1,
    skip: isSubscriptionInfoLoading,
    discountType: isEducationUser
      ? PriceDiscountType.STUDENT_DISCOUNT
      : undefined,
  });

  const handleOpenChangeSeatsCheckout = () => {
    changeActivePanelView(PaymentPageRightSidePanelType.ChangeSeats);
  };

  useEffect(() => {
    if (!!open_seats_section && isTeamsTier && !isMember) {
      handleOpenChangeSeatsCheckout();
      removeSearchParam('open_seats_section');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open_seats_section, isTeamsTier, isMember]);

  const annuallyPlanInfo = useMemo(() => {
    if (!plans) {
      return undefined;
    }

    return plans.filter(
      (item) =>
        item.plan_tier === (tier_id as string) &&
        item.period === CanonicalInterval.ANNUALLY,
    )[0];
  }, [plans, tier_id]);

  const handleCloseRightSidePanel = () => {
    changeActivePanelView(null);
  };

  const renderRightSideContent = () => {
    switch (activePanelView) {
      case PaymentPageRightSidePanelType.MonthlyToAnnually:
        // todo remove in the future if not needed
        return (
          <UpdateMonthlyToAnnuallySection
            onClose={handleCloseRightSidePanel}
            selectedPlan={annuallyPlanInfo}
          />
        );
      case PaymentPageRightSidePanelType.ChangeSeats:
        return (
          <ChangeSeatsCheckout
            onClose={handleCloseRightSidePanel}
            subscriptionInfo={subscriptionInfo}
          />
        );
      default:
        return <div />;
    }
  };

  return !isEnterpriseTier ? (
    <>
      <ManageAccountContentPage
        title="Subscription"
        errorMessage={
          subscriptionInfo?.processing_plan_change
            ? 'Payment processing in progress'
            : undefined
        }
        isWithBackButton
      >
        <div className="nj-subscription-plans">
          <SubscriptionPlansForm />

          {isTeamsTier && !isMember && (
            <>
              <hr className="divider" />
              <ChangeSeatsSection
                onOpenChangeSeatsCheckout={handleOpenChangeSeatsCheckout}
                subscriptionInfo={subscriptionInfo}
              />
            </>
          )}

          {!isMember &&
            !isAdmin &&
            isSubscribedTier &&
            !isEnterpriseTrialTier && (
              <>
                <hr className="divider" />
                <BillingInfoSection
                  isAppleOrAmpPayment={isAppleOrAmpPayment}
                  subscriptionInfo={subscriptionInfo}
                />
              </>
            )}

          {isTeamsTier && !isOwner && (
            <>
              <hr className="divider" />
              <LeaveWorkspaceSection />
            </>
          )}

          <>
            <hr className="divider" />
            <ResourcesSection />
          </>
        </div>
      </ManageAccountContentPage>
      <AppearingFromRight
        width={320}
        inProp={!!activePanelView}
        nodeRef={checkoutContentRef}
        enableTabletMode={false}
      >
        {renderRightSideContent()}
      </AppearingFromRight>
    </>
  ) : (
    <ManageAccountContentPage title="Subscription">
      <div className="enterprise-root">
        <h4 className="subtitle">Your Ninja subscription</h4>
        <div className="plan">
          <p>Plan</p>
          <p className="nj-subscription-plans-form--value">
            <LabelEnterprise />
          </p>
        </div>
      </div>
    </ManageAccountContentPage>
  );
};
